// mission-initializer-preflight.ts
//
// [B1] `autopilot init` preflight checks that must run BEFORE any side
// effect (no control root, no lock, no journal intent — a rejected init must
// leave no `missions/<id>/` behind, or `resolveMission` sees two missions
// forever). Order matters: `source_repo` must be absolute + an existing git
// repo (D6) BEFORE base_sha ever spawns `git` against it — a relative/missing
// `source_repo` used to surface as an opaque `spawn git ENOENT` inside
// `hasHead`. base_sha is then validated (regex, `hasHead`/`cat-file -e`)
// BEFORE it is used as a `git show`/`rev` argument, so the blueprint check
// below always runs against a commit provably real — at THAT commit, not
// HEAD which may have moved past base_sha (AC-03). Pack playbook check runs
// last (cheapest to explain, no git involved).
import { isAbsolute, join } from "node:path";
import { pathExists } from "../filesystem-helpers.js";
import { loadEffectiveConfig } from "./effective-config-loader.js";
import { validateBlueprintSourcePath } from "./mission-blueprint-binding.js";
import { verifyDeclaredBlueprintAtInit } from "./mission-blueprint-committed-bytes-check.js";
import { defaultRunner, type GitRunner, hasHead } from "./mission-initializer-worktree.js";
import { PRINCIPLES_RELATIVE_PATH } from "./run-command-prompt-builder.js";
import type { InitRequest } from "./schemas/init-schema.js";

/** Empty-repo base_sha convention (unborn HEAD) — the only base with no committed blueprint to verify. */
export const NULL_OID = "0".repeat(40);
const SHA_RE = /^(?:[0-9a-f]{40}|[0-9a-f]{64})$/;

/**
 * D6: `source_repo` must be an absolute path that exists and is a git repo —
 * checked BEFORE any `git` spawn against it (guide already requires
 * absolute). Returns a problem message, or `undefined` when usable.
 */
export async function assertSourceRepoUsable(
  sourceRepo: string,
  runner: GitRunner = defaultRunner,
): Promise<string | undefined> {
  if (!isAbsolute(sourceRepo)) {
    return `repo.source_repo phải là đường dẫn tuyệt đối, nhận ${JSON.stringify(sourceRepo)}.`;
  }
  if (!(await pathExists(sourceRepo))) {
    return `repo.source_repo ${JSON.stringify(sourceRepo)} không tồn tại.`;
  }
  const check = await runner(["rev-parse", "--git-dir"], sourceRepo);
  if (check.status !== 0) {
    return `repo.source_repo ${JSON.stringify(sourceRepo)} không phải git repo (git rev-parse --git-dir thất bại: ${check.stderr.trim()}).`;
  }
  return undefined;
}

export interface MissionInitPreflightInput {
  request: InitRequest;
  packSourceDir: string;
  gitRunner?: GitRunner;
}

/**
 * Returns a problem message when preflight should reject the request (no
 * mission dir has been created yet), `undefined` when it may proceed.
 */
export async function runMissionInitPreflight(
  input: MissionInitPreflightInput,
): Promise<string | undefined> {
  const { repo, blueprint } = input.request;
  const runner = input.gitRunner ?? defaultRunner;

  // (0) D6: source_repo absolute + existing + a real git repo — BEFORE any
  // git spawn against it (base_sha check below is the first such spawn).
  const sourceRepoProblem = await assertSourceRepoUsable(repo.source_repo, runner);
  if (sourceRepoProblem !== undefined) return sourceRepoProblem;

  // (a) base_sha shape — lowercased once here; this IS the value used below
  // and by every downstream consumer (worktree, blueprint rev).
  const baseSha = repo.base_sha.toLowerCase();
  if (!SHA_RE.test(baseSha)) {
    return `repo.base_sha ${JSON.stringify(repo.base_sha)} không phải commit sha hex 40/64 ký tự — init dừng trước khi tạo mission.`;
  }

  // (b) Repo state vs. base_sha. Empty repo (no HEAD yet) only ever accepts
  // the null-oid convention (today's unborn-HEAD path) — nothing is
  // committed, so there is no blueprint bytes to check either; only the
  // declared path shape is still validated (Q2, keeps unborn-HEAD's existing
  // guarantee that source_path itself can't escape the repo).
  if (!(await hasHead(repo.source_repo, runner))) {
    if (baseSha !== NULL_OID) {
      return `repo.base_sha ${baseSha} không tồn tại: ${repo.source_repo} chưa có commit — repo rỗng chỉ nhận null-oid ${NULL_OID}.`;
    }
    const pathProblem = validateBlueprintSourcePath(blueprint.source_path);
    if (pathProblem !== undefined) return pathProblem;
    return undefined;
  }

  // (c) base_sha must be a commit that actually exists in this repo.
  const check = await runner(["cat-file", "-e", `${baseSha}^{commit}`], repo.source_repo);
  if (check.status !== 0) {
    return `repo.base_sha ${baseSha} không phải commit tồn tại trong ${repo.source_repo} (git cat-file -e ${baseSha}^{commit}) — sửa base_sha rồi init lại.`;
  }

  // [B1] Blueprint hash + non-goals must hold at base_sha, not HEAD — `rev`
  // is always this already-validated sha, never a caller-supplied string.
  const blueprintProblem = await verifyDeclaredBlueprintAtInit({
    sourceRepo: repo.source_repo,
    sourcePath: blueprint.source_path,
    declaredSha256: blueprint.bytes_sha256,
    rev: baseSha,
  });
  if (blueprintProblem !== undefined) return blueprintProblem;

  // (d) Pack must carry the always-required playbook (AC-04) — checked last,
  // no git involved.
  if (!(await pathExists(join(input.packSourceDir, PRINCIPLES_RELATIVE_PATH)))) {
    return `Pack ${input.packSourceDir} thiếu playbook bắt buộc "${PRINCIPLES_RELATIVE_PATH}" — cài pack có autopilot: \`avatar sync --local <clone team-ai-pack nhánh feat/autopilot-v1>\` hoặc pack -beta.N, rồi init lại.`;
  }

  return undefined;
}

/**
 * Config preflight, also before any side effect (readiness review 2026-09-29:
 * a bad config used to leave `missions/<id>/` behind). H10: the loader itself
 * refuses `permissions_allow_fallback=true` (permissionsFallbackProblem).
 * Returns a `config_invalid: …` message or `undefined`.
 */
export async function findInitConfigProblem(globalConfigPath: string): Promise<string | undefined> {
  const cfg = await loadEffectiveConfig({ globalConfigPath });
  return cfg.ok ? undefined : `config_invalid: ${cfg.message}`;
}
